import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import dayjs from "dayjs";
import { MAGENTO_ORDERS_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";
import AppTable from "../../common/app-table";
import { formatWithTzLabel } from "../../utils/dateUtils";

const statusChip = (status) => {
  if (!status) return "--";
  const color =
    status === "complete"
      ? "success"
      : status === "processing"
        ? "primary"
        : status === "canceled"
          ? "error"
          : "default";
  return <Chip variant="outlined" color={color} label={status} size="small" />;
};

function MagentoOrdersTable() {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [ordersData, setOrdersData] = useState([]);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const fetchMagentoOrders = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    const offset = controller.page * controller.rowsPerPage;

    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-tenant-ID": tenant,
      };
      const response = await fetch(
        `${MAGENTO_ORDERS_ENDPOINT}?limit=${controller.rowsPerPage}&offset=${offset}`,
        { headers },
      );
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === "user_inactive") {
          auth.logOut();
          navigate("/login");
        }
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();
      setOrdersData(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching Magento orders:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMagentoOrders();
  }, [controller]);

  const columns = [
    { id: "magento_entity_id", label: "Order ID" },
    {
      id: "status",
      label: "Status",
      render: (o) => statusChip(o.status),
    },
    { id: "store_name", label: "Store" },
    {
      id: "customer_name",
      label: "Customer",
      render: (o) =>
        [o.customer_firstname, o.customer_lastname].filter(Boolean).join(" ") ||
        "--",
    },
    { id: "customer_email", label: "Email" },
    {
      id: "matched_phone_field",
      label: "Matched Phone",
      render: (o) => o.matched_phone_field || "--",
    },
    {
      id: "order_created_at",
      label: "Order Date",
      render: (o) =>
        o.order_created_at
          ? formatWithTzLabel(o.order_created_at, "DD/MM/YYYY HH:mm")
          : "--",
    },
    {
      id: "grand_total",
      label: "Grand Total",
      render: (o) => (o.grand_total != null ? `$${o.grand_total}` : "--"),
    },
  ];

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <AppTable
        columns={columns}
        rows={ordersData}
        rowKey={(o) => o.id}
        loading={loading}
        emptyMessage="No Magento orders found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20]}
        onPageChange={(e, newPage) =>
          setController((p) => ({ ...p, page: newPage }))
        }
        onRowsPerPageChange={(e) =>
          setController((p) => ({
            ...p,
            rowsPerPage: parseInt(e.target.value, 20),
            page: 0,
          }))
        }
      />
    </Paper>
  );
}

export default MagentoOrdersTable;
